/**
 * Universal Location Input - Backward Compatibility Wrappers
 * These components maintain backward compatibility during the transition period
 */

"use client";
import React from 'react';
import { createLogger } from '@/utils/logger/devLogger';
import UniversalLocationInput from './index';
import type { UniversalLocationInputProps, UniversalLocationData } from './types';

const compatibilityLogger = createLogger('LocationCompatibility');

// ============================================================================
// GooglePlacesAddressForm Compatibility Wrapper
// ============================================================================

export interface GoogleAddressFields {
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface GooglePlacesAddressFormProps {
  onAddressChange: (address: GoogleAddressFields) => void;
  initialValues?: Partial<GoogleAddressFields>;
  errors?: {
    streetAddress?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  className?: string;
}

export const GooglePlacesAddressForm: React.FC<GooglePlacesAddressFormProps> = (props) => {
  compatibilityLogger.debug('🔄 GooglePlacesAddressForm wrapper called');
  
  const handleLocationChange = (location: UniversalLocationData) => {
    const addressData: GoogleAddressFields = {
      streetAddress: location.streetAddress,
      addressLine2: location.addressLine2 || '',
      city: location.city,
      state: location.state,
      zipCode: location.zipCode
    };
    props.onAddressChange(addressData);
  };

  return (
    <UniversalLocationInput
      onLocationChange={handleLocationChange}
      initialValues={props.initialValues}
      errors={props.errors}
      onBlur={props.onBlur}
      className={props.className}
    />
  );
};

// ============================================================================
// AddressFieldSet Compatibility Wrapper
// ============================================================================

export interface AddressFieldSetProps {
  onAddressChange: (address: GoogleAddressFields) => void;
  initialValues?: Partial<GoogleAddressFields>;
  errors?: {
    streetAddress?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  className?: string;
  required?: boolean;
}

export const AddressFieldSet: React.FC<AddressFieldSetProps> = (props) => {
  compatibilityLogger.debug('🔄 AddressFieldSet wrapper called');
  
  const handleLocationChange = (location: UniversalLocationData) => {
    const addressData: GoogleAddressFields = {
      streetAddress: location.streetAddress,
      addressLine2: location.addressLine2 || '',
      city: location.city,
      state: location.state,
      zipCode: location.zipCode
    };
    props.onAddressChange(addressData);
  };

  return (
    <UniversalLocationInput
      
      onLocationChange={handleLocationChange}
      initialValues={props.initialValues}
      errors={props.errors}
      onBlur={props.onBlur}
      className={props.className}
      required={props.required}
    />
  );
};

// ============================================================================
// GoogleAddressAutoComplete Compatibility Wrapper  
// ============================================================================

export interface GoogleAddressAutoCompleteProps {
  onAddressChange: (address: GoogleAddressFields) => void;
  initialValues?: Partial<GoogleAddressFields>;
  errors?: {
    streetAddress?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  className?: string;
  required?: boolean;
}

export const GoogleAddressAutoComplete: React.FC<GoogleAddressAutoCompleteProps> = (props) => {
  compatibilityLogger.debug('🔄 GoogleAddressAutoComplete wrapper called');
  
  const handleLocationChange = (location: UniversalLocationData) => {
    const addressData: GoogleAddressFields = {
      streetAddress: location.streetAddress,
      addressLine2: location.addressLine2 || '',
      city: location.city,
      state: location.state,
      zipCode: location.zipCode
    };
    props.onAddressChange(addressData);
  };

  return (
    <UniversalLocationInput
      
      onLocationChange={handleLocationChange}
      initialValues={props.initialValues}
      errors={props.errors}
      onBlur={props.onBlur}
      className={props.className}
      required={props.required}
    />
  );
};

// ============================================================================
// AddressEditCard Compatibility Wrapper
// ============================================================================

export interface AddressEditCardProps {
  onAddressChange: (address: GoogleAddressFields) => void;
  initialValues?: Partial<GoogleAddressFields>;
  errors?: {
    streetAddress?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  className?: string;
  required?: boolean;
  mode?: 'create' | 'edit';
}

export const AddressEditCard: React.FC<AddressEditCardProps> = (props) => {
  compatibilityLogger.debug('🔄 AddressEditCard wrapper called');
  
  const handleLocationChange = (location: UniversalLocationData) => {
    const addressData: GoogleAddressFields = {
      streetAddress: location.streetAddress,
      addressLine2: location.addressLine2 || '',
      city: location.city,
      state: location.state,
      zipCode: location.zipCode
    };
    props.onAddressChange(addressData);
  };

  return (
    <UniversalLocationInput
      
      onLocationChange={handleLocationChange}
      initialValues={props.initialValues}
      errors={props.errors}
      onBlur={props.onBlur}
      className={props.className}
      required={props.required}
    />
  );
};

// ============================================================================
// GooglePlacesSearch Compatibility Wrapper
// ============================================================================

export interface AddressFields {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  name?: string; // For venues/businesses
}

export interface GooglePlacesSearchProps {
  onAddressSelect: (address: AddressFields) => void;
  placeholder?: string;
  className?: string;
  searchType?: 'address' | 'place' | 'both';
}

export const GooglePlacesSearch: React.FC<GooglePlacesSearchProps> = (props) => {
  compatibilityLogger.debug('🔄 GooglePlacesSearch wrapper called');
  
  const handleLocationChange = (location: UniversalLocationData) => {
    // Convert UniversalLocationData to AddressFields format
    const addressFields: AddressFields = {
      street: location.streetAddress,
      city: location.city,
      state: location.state,
      zipCode: location.zipCode,
      name: location.venueName // Extract venue name for venues/businesses
    };
    
    props.onAddressSelect(addressFields);
  };

  return (
    <UniversalLocationInput
      onLocationChange={handleLocationChange}
      placeholder={props.placeholder}
      className={props.className}
      showHeader={false}
    />
  );
};

// ============================================================================
// LocationSearch Compatibility Wrapper
// ============================================================================

export interface LocationSearchProps {
  onLocationSelect: (location: any) => void;
  placeholder?: string;
  className?: string;
}

export const LocationSearch: React.FC<LocationSearchProps> = (props) => {
  compatibilityLogger.debug('🔄 LocationSearch wrapper called');
  
  const handleLocationChange = (location: UniversalLocationData) => {
    props.onLocationSelect({
      name: location.venueName || location.streetAddress,
      address: [
        location.streetAddress,
        location.addressLine2,
        `${location.city}, ${location.state} ${location.zipCode}`
      ].filter(Boolean).join(', '),
      ...location
    });
  };

  return (
    <UniversalLocationInput
      
      onLocationChange={handleLocationChange}
      placeholder={props.placeholder || "Search for a location..."}
      className={props.className}
    />
  );
};

// ============================================================================
// AddressFields Compatibility Wrapper
// ============================================================================

export interface AddressFieldsProps {
  onAddressChange: (address: GoogleAddressFields) => void;
  initialValues?: Partial<GoogleAddressFields>;
  errors?: {
    streetAddress?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  className?: string;
  required?: boolean;
}

export const AddressFields: React.FC<AddressFieldsProps> = (props) => {
  compatibilityLogger.debug('🔄 AddressFields wrapper called');
  
  const handleLocationChange = (location: UniversalLocationData) => {
    const addressData: GoogleAddressFields = {
      streetAddress: location.streetAddress,
      addressLine2: location.addressLine2 || '',
      city: location.city,
      state: location.state,
      zipCode: location.zipCode
    };
    props.onAddressChange(addressData);
  };

  return (
    <UniversalLocationInput
      
      onLocationChange={handleLocationChange}
      initialValues={props.initialValues}
      errors={props.errors}
      onBlur={props.onBlur}
      className={props.className}
      required={props.required}
    />
  );
};